import React, {FC, useCallback, useEffect, useReducer, useRef} from 'react';

type EndlessLoopBreakerConfig = {
  hook: () => void;
  name: string;
};

export const createEndlessLoopBreakerComponent = ({
  hook: useHook,
  name,
}: EndlessLoopBreakerConfig) => {
  const EndlessLoopBreakerInner: FC<{stopHandler: () => void}> = ({
    stopHandler,
  }) => {
    useHook();
    useEndlessLoopBreaker(stopHandler);
    return null;
  };
  const EndlessLoopBreakerWrapper: FC<{
    endlessLoopCallback?: (name: string) => void;
  }> = ({endlessLoopCallback}) => {
    const [shouldStop, stop] = useReducer(() => true, false);
    const stopHandler = useCallback(() => {
      stop();
      endlessLoopCallback?.(name);
    }, [stop, endlessLoopCallback]);

    if (shouldStop) {
      return null;
    }

    return <EndlessLoopBreakerInner stopHandler={stopHandler} />;
  };
  return EndlessLoopBreakerWrapper;
};

const MAX_CALLS_PER_INTERVAL = 25;
const INTERVAL = 1000;
const useEndlessLoopBreaker = (stopHandler: () => void) => {
  const callCount = useRef(0);
  const isStopped = useRef(false);
  const lastCallDate = useRef(Date.now());
  useEffect(() => {
    const now = Date.now();
    if (lastCallDate.current + INTERVAL > now) {
      callCount.current++;
    } else {
      callCount.current = 1;
    }
    lastCallDate.current = now;
  });

  const reachedThreshold = callCount.current > MAX_CALLS_PER_INTERVAL;
  useEffect(() => {
    if (reachedThreshold && !isStopped.current) {
      stopHandler();
      isStopped.current = true;
    }
  }, [reachedThreshold, stopHandler]);
};
